import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, FileText, UserCheck } from 'lucide-react';

const Legal = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-4xl font-bold">{t('legal.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('legal.subtitle')}
          </p>
        </div>

        <Tabs defaultValue="terms">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('legal.tabs.terms')}
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {t('legal.tabs.privacy')}
            </TabsTrigger>
            <TabsTrigger value="cookies" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              {t('legal.tabs.cookies')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="terms" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('legal.terms.title')}</CardTitle>
              </CardHeader>
              <CardContent className="prose max-w-none">
                <p><strong>{t('legal.terms.lastUpdated')}</strong></p>

                <h2>{t('legal.terms.sections.introduction.title')}</h2>
                <p>{t('legal.terms.sections.introduction.content')}</p>

                <h2>{t('legal.terms.sections.accountRegistration.title')}</h2>
                <p>{t('legal.terms.sections.accountRegistration.content')}</p>

                <h2>{t('legal.terms.sections.subscriptionAndPayments.title')}</h2>
                <p>{t('legal.terms.sections.subscriptionAndPayments.content')}</p>

                <h2>{t('legal.terms.sections.userContent.title')}</h2>
                <p>{t('legal.terms.sections.userContent.content')}</p>

                <h2>{t('legal.terms.sections.prohibitedActivities.title')}</h2>
                <p>{t('legal.terms.sections.prohibitedActivities.content')}</p>

                <h2>{t('legal.terms.sections.termination.title')}</h2>
                <p>{t('legal.terms.sections.termination.content')}</p>

                <h2>{t('legal.terms.sections.disclaimerOfWarranties.title')}</h2>
                <p>{t('legal.terms.sections.disclaimerOfWarranties.content')}</p>

                <h2>{t('legal.terms.sections.limitationOfLiability.title')}</h2>
                <p>{t('legal.terms.sections.limitationOfLiability.content')}</p>

                <h2>{t('legal.terms.sections.changesToTerms.title')}</h2>
                <p>{t('legal.terms.sections.changesToTerms.content')}</p>

                <h2>{t('legal.terms.sections.governingLaw.title')}</h2>
                <p>{t('legal.terms.sections.governingLaw.content')}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="privacy" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('legal.privacy.title')}</CardTitle>
              </CardHeader>
              <CardContent className="prose max-w-none">
                <p><strong>{t('legal.privacy.lastUpdated')}</strong></p>

                <h2>{t('legal.privacy.sections.informationWeCollect.title')}</h2>
                <p>{t('legal.privacy.sections.informationWeCollect.content')}</p>

                <h2>{t('legal.privacy.sections.howWeUseYourInformation.title')}</h2>
                <p>{t('legal.privacy.sections.howWeUseYourInformation.content')}</p>

                <h2>{t('legal.privacy.sections.sharingOfInformation.title')}</h2>
                <p>{t('legal.privacy.sections.sharingOfInformation.content')}</p>

                <h2>{t('legal.privacy.sections.security.title')}</h2>
                <p>{t('legal.privacy.sections.security.content')}</p>

                <h2>{t('legal.privacy.sections.yourChoices.title')}</h2>
                <p>{t('legal.privacy.sections.yourChoices.content')}</p>

                <h2>{t('legal.privacy.sections.cookies.title')}</h2>
                <p>{t('legal.privacy.sections.cookies.content')}</p>

                <h2>{t('legal.privacy.sections.internationalDataTransfers.title')}</h2>
                <p>{t('legal.privacy.sections.internationalDataTransfers.content')}</p>

                <h2>{t('legal.privacy.sections.childrensPrivacy.title')}</h2>
                <p>{t('legal.privacy.sections.childrensPrivacy.content')}</p>

                <h2>{t('legal.privacy.sections.changesToThisPrivacyPolicy.title')}</h2>
                <p>{t('legal.privacy.sections.changesToThisPrivacyPolicy.content')}</p>

                <h2>{t('legal.privacy.sections.contactUs.title')}</h2>
                <p>{t('legal.privacy.sections.contactUs.content')}</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cookies" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('legal.cookies.title')}</CardTitle>
              </CardHeader>
              <CardContent className="prose max-w-none">
                <p><strong>{t('legal.cookies.lastUpdated')}</strong></p>

                <h2>{t('legal.cookies.sections.whatAreCookies.title')}</h2>
                <p>{t('legal.cookies.sections.whatAreCookies.content')}</p>

                <h2>{t('legal.cookies.sections.howWeUseCookies.title')}</h2>
                <p>{t('legal.cookies.sections.howWeUseCookies.content')}</p>

                <h2>{t('legal.cookies.sections.typesOfCookiesWeUse.title')}</h2>
                <p>{t('legal.cookies.sections.typesOfCookiesWeUse.content')}</p>

                <h2>{t('legal.cookies.sections.thirdPartyCookies.title')}</h2>
                <p>{t('legal.cookies.sections.thirdPartyCookies.content')}</p>

                <h2>{t('legal.cookies.sections.managingCookies.title')}</h2>
                <p>{t('legal.cookies.sections.managingCookies.content')}</p>

                <h2>{t('legal.cookies.sections.changesToThisCookiePolicy.title')}</h2>
                <p>{t('legal.cookies.sections.changesToThisCookiePolicy.content')}</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default Legal;
